import { FC } from 'react';
import { Avatar, Box, MenuItem, Select, FormControl, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useGetProjectByIdQuery } from 'redux/apiSlices/project.slice';
import { IProjectMemberUI } from 'data/types/project.types';
import { useUpdateTaskAssignerMutation } from 'redux/apiSlices/tasks.slice';
import { ITaskAssigneeUI } from 'data/types/task.type';

const TaskAssignee: FC<{
  projectId: string;
  assignee?: ITaskAssigneeUI;
  taskId: string;
}> = ({ projectId, assignee, taskId }) => {
  const [updateAssignee] = useUpdateTaskAssignerMutation();
  const { data: projectData } = useGetProjectByIdQuery(projectId);
  const members: IProjectMemberUI[] = projectData?.members || [];

  const onUpdateAssignee = (member: IProjectMemberUI | null) => {
    updateAssignee({
      taskId,
      member,
    });
  };

  const handleChange = (userId: string) => {
    if (userId === '') {
      onUpdateAssignee(null);
    } else {
      const member = members.find(m => m.userId === userId);
      if (member) {
        onUpdateAssignee(member);
      }
    }
  };

  return (
    <>
      {!!members.length && (
        <FormControl fullWidth size="small">
          <Select
            value={assignee?.userId || ''}
            onChange={e => handleChange(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: 'background.paper',
              '& .MuiSelect-select': {
                py: 1,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            renderValue={selected => {
              if (!selected) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <PersonOutlineIcon fontSize="small" />
                    <Typography variant="body2">Unassigned</Typography>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: assignee?.avatarColor || 'primary.main',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    {assignee?.firstLetters}
                  </Avatar>
                  <Typography variant="body2" noWrap>
                    {assignee?.name || assignee?.email}
                  </Typography>
                </Box>
              );
            }}
          >
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2">Unassigned</Typography>
              </Box>
            </MenuItem>
            {members.map(member => (
              <MenuItem key={member.userId} value={member.userId}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: member.avatarColor || 'primary.main',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    {member.firstLetters}
                  </Avatar>
                  <Typography variant="body2" noWrap>
                    {member.name || member.email}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </>
  );
};

export default TaskAssignee;
